import type { StateCreator } from 'zustand';
import {
  deleteSession as apiDeleteSession,
  listSessions,
  pinSession as apiPinSession,
  updateSession as apiUpdateSession,
} from '@/lib/api/sessions.client';
import type { IChatHistory } from '@/lib/types/chat';
import {
  bumpRow,
  firstPlainPage,
  holdsChat,
  patchRow,
  rewriteListsHolding,
  withoutRow,
} from '@/lib/stores/chat-list-rows';
import { forgetPromotedDraft } from '@/lib/stores/chat-store/drafts';
import { announceChatRemoved } from '@/lib/stores/chat-store/removed-chats';
import { captureWorkspace } from '@/lib/stores/chat-store/workspace-generation';
import type { ChatState, GetChatState, SetChatState } from '@/lib/stores/chat.store';
import { sessionToHistory } from '@/lib/stores/session-history';
import { useSharedChatsStore } from '@/lib/stores/shared-chats.store';

/** How many sessions one page of the plain list holds. */
const SESSIONS_PAGE_SIZE = 50;

/**
 * The reader's own chat lists of the chat store (KAN-1266): the plain sidebar
 * page by page, the per-agent lists, and every change to a row of them.
 */
interface SessionListData {
  chatHistory: IChatHistory[];
  // Per-agent session cache (Story 23.8 follow-up). ``chatHistory`` holds
  // the plain area exactly as the server lists it (see ``ownListsWith``).
  // For the per-agent right-panel we keep a separate, agent-scoped cache
  // populated by ``loadAgentSessions(agentId)``. Mutations that affect
  // either cache (delete/rename/promote/bump) update both sides so the
  // panel stays in sync without a re-fetch.
  agentChatsById: Record<string, IChatHistory[]>;
  agentChatsLoadingIds: string[];

  // Session paging
  sessionsOffset: number;
  sessionsLimit: number;
  sessionsHasMore: boolean;
  isLoadingSessions: boolean;
  initializePromise: Promise<void> | null;
}

export interface SessionListSlice extends SessionListData {
  deleteChat: (chatId: string) => Promise<void>;
  /**
   * Drop every local trace of a chat the server has deleted: history, the
   * per-agent caches, both shared lists, the open chat if it happens to be
   * this one, and a file uploaded into it waiting in a composer.
   *
   * ``deleteChat`` (which owns the request) leaves the same state behind,
   * the rows at once, the rest once the server confirmed. The
   * ``chat.session.deleted`` event from another tab or device calls this, so
   * a chat deleted anywhere leaves exactly the same state behind here.
   */
  removeChatLocally: (chatId: string) => void;
  renameChat: (chatId: string, title: string) => Promise<void>;
  togglePin: (chatId: string, pinned: boolean) => Promise<void>;
  updateChatTitle: (chatId: string, title: string) => void;
  // Reorder a known session to the top on remote activity (a message landed
  // in another tab/device). Unlike ``applyStoredMessages`` no message list is
  // available — it only reorders + refreshes the activity timestamp.
  bumpSessionActivity: (sessionId: string) => void;

  // Initialisation at app start
  initialize: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  loadAgentSessions: (agentId: string) => Promise<void>;
  // Reflect a just-changed share state for one session: flips the ``isShared``
  // flag on the sidebar rows (so the two-person icon appears/disappears) and
  // refreshes the shared sections of every area — called right after the owner
  // adds/removes a person or creates/revokes the link, so the sidebar updates
  // without waiting for a reconnect.
  markSessionShared: (sessionId: string, isShared: boolean) => void;
}

/**
 * The lists before anything loaded, and again after a reset. The page size
 * is not part of it: a reset leaves it alone.
 */
export const initialSessionListState = (): Omit<SessionListData, 'sessionsLimit'> => ({
  chatHistory: [],
  agentChatsById: {},
  agentChatsLoadingIds: [],
  sessionsOffset: 0,
  sessionsHasMore: true,
  isLoadingSessions: false,
  initializePromise: null,
});

const deleteChat = async (set: SetChatState, get: GetChatState, chatId: string) => {
  const currentChat = get().currentChat;
  if (currentChat?.isDraft && currentChat.id === chatId) {
    set({ currentChat: null });
    return;
  }
  // Drop it locally BEFORE the request, not after. The delete is not a
  // quick call — the router clears the session's files off S3 first, and
  // that has taken 20s in the wild — and the caller has already left the
  // conversation by now. Waiting would leave the sidebar row standing all
  // that time, one click away from walking back into a chat that is
  // already closed and about to be gone (KAN-630).
  const { chatHistory, agentChatsById } = get();
  const sharedByArea = useSharedChatsStore.getState().byArea;
  const sameWorkspace = captureWorkspace();
  dropChatFromLists(set, get, chatId);
  try {
    await apiDeleteSession(chatId);
    forgetChatGone(chatId);
  } catch (error) {
    // Put the lists back — but not ``currentChat``/``activeChatId``: the
    // caller navigated away and now holds a different chat, so restoring
    // those would contradict the address bar. The caller re-syncs against
    // the server on top of this, which also settles the case where the
    // delete failed because the chat was already gone. The snapshot belongs
    // to the workspace the delete was made in: after a switch it stays away.
    if (sameWorkspace()) {
      set({ chatHistory, agentChatsById });
      useSharedChatsStore.setState({ byArea: sharedByArea });
    }
    throw error;
  }
};

/**
 * What outlives a chat's rows and must go once the server has deleted it, not
 * before: a delete that fails brings the chat back, and it must find these
 * where they were (KAN-1852).
 */
const forgetChatGone = (chatId: string) => {
  // A new-chat thread whose upload created this session opens as a fresh draft again.
  forgetPromotedDraft(chatId);
  // And a composer holding a file uploaded into it lets the file go.
  announceChatRemoved(chatId);
};

/** For the event: the server has deleted the chat already. */
const removeChatLocally = (set: SetChatState, get: GetChatState, chatId: string) => {
  forgetChatGone(chatId);
  dropChatFromLists(set, get, chatId);
};

const dropChatFromLists = (set: SetChatState, get: GetChatState, chatId: string) => {
  const state = get();
  // A chat the user had shared stays visible in "Von mir geteilt" (and a
  // chat shared with them in "Geteilt mit mir") unless it is dropped there
  // too — the row then points at a session that is gone.
  useSharedChatsStore.getState().removeChat(chatId);
  const lists = rewriteListsHolding(state, chatId, withoutRow(chatId));
  const affected =
    lists.chatHistory !== state.chatHistory ||
    lists.agentChatsById !== state.agentChatsById ||
    state.currentChat?.id === chatId ||
    state.activeChatId === chatId;
  // The delete event reaches the tab that triggered it too, where this
  // has already run. Bail out instead of handing every subscriber a set
  // of fresh-but-identical arrays to re-render on.
  if (!affected) return;
  set({
    ...lists,
    currentChat: state.currentChat?.id === chatId ? null : state.currentChat,
    activeChatId: state.activeChatId === chatId ? null : state.activeChatId,
  });
};

const renameChat = async (get: GetChatState, chatId: string, title: string) => {
  const trimmedTitle = title.trim();
  if (!trimmedTitle) return;

  await apiUpdateSession(chatId, { title: trimmedTitle });
  get().updateChatTitle(chatId, trimmedTitle);
};

const togglePin = async (set: SetChatState, chatId: string, pinned: boolean) => {
  await apiPinSession(chatId, pinned);
  useSharedChatsStore.getState().patchChat(chatId, { isPinned: pinned });
  set((state) => rewriteListsHolding(state, chatId, patchRow(chatId, { isPinned: pinned })));
};

const updateChatTitle = (set: SetChatState, chatId: string, title: string) => {
  useSharedChatsStore.getState().patchChat(chatId, { title });
  set((state) => ({
    ...rewriteListsHolding(state, chatId, patchRow(chatId, { title })),
    currentChat:
      state.currentChat?.id === chatId ? { ...state.currentChat, title } : state.currentChat,
  }));
};

// Remote activity (``chat.session.activity`` from another tab/device): a
// new message landed in a chat we list. The move ``applyStoredMessages``
// makes for a newer message, without a message list to date it by.
const bumpSessionActivity = (set: SetChatState, sessionId: string) => {
  set((state) => rewriteListsHolding(state, sessionId, bumpRow(sessionId)));
};

const initialize = async (set: SetChatState, get: GetChatState) => {
  // Dedup: if already running, return existing promise
  const existing = get().initializePromise;
  if (existing) return existing;

  // A workspace switch forgets this load but cannot stop it. Its answer, its
  // failure and its end belong to the workspace it was asked in, and none of
  // them is written once the tab has moved on.
  const sameWorkspace = captureWorkspace();
  const promise = (async () => {
    // Load the sessions (route pages decide which chat they open)
    try {
      const limit = get().sessionsLimit;
      const { sessions, pagination } = await listSessions({ limit, offset: 0 });
      if (sameWorkspace()) set(firstPlainPage(sessions, pagination, limit));
      // No automatic loadChat(): route pages do that based on the URL
    } catch {
      // On errors leave the store empty
      if (sameWorkspace()) {
        set({ currentChat: null, chatHistory: [], sessionsOffset: 0, sessionsHasMore: true });
      }
    } finally {
      if (sameWorkspace()) set({ initializePromise: null });
    }
  })();

  set({ initializePromise: promise });
  return promise;
};

const refreshSessions = async (set: SetChatState, get: GetChatState) => {
  const existing = get().initializePromise;
  if (existing) {
    await existing;
  }
  set({ isLoadingSessions: true });
  try {
    const limit = get().sessionsLimit;
    const { sessions, pagination } = await listSessions({ limit, offset: 0 });
    set({ ...firstPlainPage(sessions, pagination, limit), isLoadingSessions: false });
  } catch {
    set({ isLoadingSessions: false });
  }
};

const loadMoreSessions = async (set: SetChatState, get: GetChatState) => {
  const { isLoadingSessions, sessionsHasMore, sessionsOffset, sessionsLimit } = get();
  if (isLoadingSessions || !sessionsHasMore) return;
  set({ isLoadingSessions: true });
  try {
    const { sessions: next, pagination } = await listSessions({
      limit: sessionsLimit,
      offset: sessionsOffset,
    });
    set((state) => {
      const appended = next
        .filter((s) => !holdsChat(state.chatHistory, s.id))
        .map(sessionToHistory);
      return {
        chatHistory: [...state.chatHistory, ...appended],
        sessionsOffset: pagination
          ? pagination.offset + pagination.count
          : state.sessionsOffset + next.length,
        sessionsHasMore: pagination?.hasMore ?? next.length === state.sessionsLimit,
        isLoadingSessions: false,
      };
    });
  } catch {
    set({ isLoadingSessions: false });
  }
};

const loadAgentSessions = async (set: SetChatState, get: GetChatState, agentId: string) => {
  if (!agentId) return;
  // Dedup concurrent calls — the agent layout fires this on mount,
  // a route remount inside the same agent (e.g. tab change) would
  // otherwise refetch unnecessarily.
  if (get().agentChatsLoadingIds.includes(agentId)) return;
  set((state) => ({
    agentChatsLoadingIds: [...state.agentChatsLoadingIds, agentId],
  }));
  try {
    const { sessions } = await listSessions({ limit: 100, offset: 0, agentId });
    set((state) => ({
      agentChatsById: { ...state.agentChatsById, [agentId]: sessions.map(sessionToHistory) },
      agentChatsLoadingIds: state.agentChatsLoadingIds.filter((id) => id !== agentId),
    }));
  } catch {
    set((state) => ({
      agentChatsLoadingIds: state.agentChatsLoadingIds.filter((id) => id !== agentId),
    }));
  }
};

const markSessionShared = (set: SetChatState, sessionId: string, isShared: boolean) => {
  set((state) => rewriteListsHolding(state, sessionId, patchRow(sessionId, { isShared })));
  // Refresh "Von mir geteilt" in every area held. A forced reload, so a
  // mount-time load still settling cannot answer for the new state.
  useSharedChatsStore.getState().reloadAll();
};

export const createSessionListSlice: StateCreator<ChatState, [], [], SessionListSlice> = (
  set,
  get
) => ({
  ...initialSessionListState(),
  sessionsLimit: SESSIONS_PAGE_SIZE,
  deleteChat: (chatId) => deleteChat(set, get, chatId),
  removeChatLocally: (chatId) => removeChatLocally(set, get, chatId),
  renameChat: (chatId, title) => renameChat(get, chatId, title),
  togglePin: (chatId, pinned) => togglePin(set, chatId, pinned),
  updateChatTitle: (chatId, title) => updateChatTitle(set, chatId, title),
  bumpSessionActivity: (sessionId) => bumpSessionActivity(set, sessionId),
  initialize: () => initialize(set, get),
  refreshSessions: () => refreshSessions(set, get),
  loadMoreSessions: () => loadMoreSessions(set, get),
  loadAgentSessions: (agentId) => loadAgentSessions(set, get, agentId),
  markSessionShared: (sessionId, isShared) => markSessionShared(set, sessionId, isShared),
});
